/**
 * Cliente API para los endpoints de Data Health.
 *
 * Backend spec: docs/architecture/VISIBILITY_AND_HISTORY_SPEC.md
 * UX spec:      docs/architecture/DATA_HEALTH_PANEL_SPEC.md
 *
 * IMPORTANTE: el backend de visibility/history está siendo construido en
 * paralelo por otro sub-agente. Si los endpoints no existen aún, las llamadas
 * van a tirar 404 — la UI debe manejarlo (toast "Backend pendiente de deploy")
 * sin crashear.
 */
import api from '../utils/api';
import type {
  BulkActionResult,
  DataHealthItem,
  HiddenReason,
  HiddenStats,
  HistoryEvent,
  ListFilters,
  ListResponse,
} from '../types/dataHealth';

function buildQuery(params: Record<string, unknown>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    usp.append(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

// ----------------------------------------------------------------------------
// Lists
// ----------------------------------------------------------------------------

/**
 * Lista productos ocultos del cliente.
 *
 * Endpoint asumido: GET /api/clients/:id/catalog?visibility=hidden&...
 * Si el backend devuelve 404, la UI ya muestra empty state defensivo.
 */
export async function listHidden(
  clientId: string,
  filters: ListFilters = {}
): Promise<ListResponse> {
  const qs = buildQuery({
    visibility: 'hidden',
    page: filters.page ?? 1,
    limit: filters.limit ?? 50,
    reason: filters.reason && filters.reason !== 'all' ? filters.reason : undefined,
    search: filters.search,
    sitio: filters.sitio,
    marca: filters.marca,
    subcat: filters.subcat,
    sortBy: filters.sortBy || undefined,
    sortDir: filters.sortDir || undefined,
  });
  const res = await api.get(`/api/clients/${clientId}/catalog${qs}`, {
    headers: { 'x-client-id': clientId },
  });
  return normalizeListResponse(res.data);
}

/**
 * Lista productos "dudosos" (catalog visible pero con señales sospechosas:
 * subcategory IS NULL OR confidence_score < 0.7 OR validación V2 con flags).
 */
export async function listDubious(
  clientId: string,
  filters: ListFilters = {}
): Promise<ListResponse> {
  const qs = buildQuery({
    visibility: 'visible',
    dubious: 1,
    page: filters.page ?? 1,
    limit: filters.limit ?? 50,
    reason: filters.dubiousReason && filters.dubiousReason !== 'all' ? filters.dubiousReason : undefined,
    search: filters.search,
    sitio: filters.sitio,
    marca: filters.marca,
    subcat: filters.subcat,
    sortBy: filters.sortBy || undefined,
    sortDir: filters.sortDir || undefined,
  });
  const res = await api.get(`/api/clients/${clientId}/catalog${qs}`, {
    headers: { 'x-client-id': clientId },
  });
  return normalizeListResponse(res.data);
}

function normalizeListResponse(data: unknown): ListResponse {
  const d = (data && typeof data === 'object' ? data : {}) as Record<string, unknown>;
  const items = Array.isArray(d.items) ? (d.items as DataHealthItem[]) : [];
  const total = typeof d.total === 'number' ? d.total : items.length;
  const page = typeof d.page === 'number' ? d.page : 1;
  const limit = typeof d.limit === 'number' ? d.limit : 50;
  const hasMore = typeof d.hasMore === 'boolean' ? d.hasMore : items.length === limit;
  return { items, total, page, limit, hasMore };
}

// ----------------------------------------------------------------------------
// Stats
// ----------------------------------------------------------------------------

export async function getHiddenStats(clientId: string): Promise<HiddenStats> {
  const res = await api.get(`/api/clients/${clientId}/catalog/hidden-stats`, {
    headers: { 'x-client-id': clientId },
  });
  const d = res.data || {};
  return {
    visible: typeof d.visible === 'number' ? d.visible : 0,
    hidden_total: typeof d.hidden_total === 'number' ? d.hidden_total : 0,
    by_reason: d.by_reason || {},
  };
}

// ----------------------------------------------------------------------------
// Bulk actions
// ----------------------------------------------------------------------------

export async function bulkHide(
  clientId: string,
  ids: string[],
  reason: HiddenReason,
  note?: string
): Promise<BulkActionResult> {
  const res = await api.post(
    `/api/clients/${clientId}/catalog/bulk-hide`,
    { ids, reason, note },
    { headers: { 'x-client-id': clientId } }
  );
  return res.data || { ok: false };
}

export async function bulkUnhide(
  clientId: string,
  ids: string[],
  note?: string
): Promise<BulkActionResult> {
  const res = await api.post(
    `/api/clients/${clientId}/catalog/bulk-unhide`,
    { ids, note },
    { headers: { 'x-client-id': clientId } }
  );
  return res.data || { ok: false };
}

// ----------------------------------------------------------------------------
// History
// ----------------------------------------------------------------------------

export async function getHistory(
  clientId: string,
  idCatalogo: string,
  opts: { limit?: number; before?: string } = {}
): Promise<{ events: HistoryEvent[]; hasMore: boolean }> {
  const qs = buildQuery({ limit: opts.limit ?? 200, before: opts.before });
  const res = await api.get(
    `/api/clients/${clientId}/catalog/${encodeURIComponent(idCatalogo)}/history${qs}`,
    { headers: { 'x-client-id': clientId } }
  );
  const d = res.data || {};
  const raw = Array.isArray(d.events) ? d.events : [];
  const events: HistoryEvent[] = raw.map((e: Record<string, unknown>) => ({
    id: String(e.id ?? e.event_id ?? ''),
    eventType: (e.event_type ?? e.eventType ?? 'manual_edit') as HistoryEvent['eventType'],
    actor: (e.actor ?? e.actor_uid ?? e.actor_name ?? null) as string | null,
    actorKind: (e.actor_kind ?? e.actorKind ?? 'user') as HistoryEvent['actorKind'],
    payload: (e.payload ?? {}) as Record<string, unknown>,
    createdAt: String(e.event_at ?? e.createdAt ?? ''),
  }));
  return { events, hasMore: !!d.has_more };
}

// ----------------------------------------------------------------------------
// Re-cataloga (bulk LLM run)
// ----------------------------------------------------------------------------

/**
 * Encola un run de re-catalogado sobre los ids dados.
 *
 * Reusa el endpoint existente `POST /catalog/run-batch` con `mode: 'force'`
 * y filtro por idCatalogos. Devuelve `{ runId }` para que la UI pueda
 * redirigir a Catalogador IA → Histórico.
 *
 * Si el endpoint no acepta `ids` explícitos todavía, esta call va a fallar
 * 400/404 y la UI muestra un toast informativo.
 */
export async function triggerRecatalog(
  clientId: string,
  ids: string[]
): Promise<{ runId: string }> {
  const res = await api.post(
    `/api/clients/${clientId}/catalog/run-batch`,
    { mode: 'force', idCatalogos: ids },
    { headers: { 'x-client-id': clientId } }
  );
  const d = res.data || {};
  return { runId: String(d.runId ?? d.run_id ?? '') };
}

// ----------------------------------------------------------------------------
// Revision Queue (MVP — superadmin only)
// ----------------------------------------------------------------------------
// Backend: server/routes/dataHealthRoutes.js
// Migracion: scripts/migrations/2026-06-08-revision-queue.js

export type RevisionScenario =
  | 'rejected'
  | 'low_conf'
  | 'brand_ghost'
  | 'misspelling'
  | 'toma_vs_catalog'
  | 'hidden_classifier'
  | 'manual_audit'
  | 'url_unverified'
  | 'inbox'
  | 'brand_drift';

export type RevisionDecision =
  | 'approve_hide'
  | 'revive'
  | 'catalog_manual'
  | 'flag_for_review';

export interface RevisionQueueFilters {
  page?: number;
  pageSize?: number;
  search?: string;
  marca?: string;
  sitio?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface RevisionQueueResponse {
  items: DataHealthItem[];
  total: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
  scenario: RevisionScenario;
}

export interface RevisionHistoryEvent {
  kind: 'history' | 'feedback';
  id: number;
  at: string;
  // history-specific
  event_type?: string;
  actor?: string | null;
  actor_type?: string | null;
  payload?: Record<string, unknown>;
  // feedback-specific
  scenario?: RevisionScenario;
  decision?: RevisionDecision;
  comment?: string | null;
  reviewer?: string | null;
  snapshot?: Record<string, unknown> | null;
}

export async function listRevisionQueue(
  clientId: string,
  scenario: RevisionScenario,
  filters: RevisionQueueFilters = {}
): Promise<RevisionQueueResponse> {
  const qs = buildQuery({
    scenario,
    page: filters.page ?? 1,
    pageSize: filters.pageSize ?? 50,
    search: filters.search,
    marca: filters.marca,
    sitio: filters.sitio,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
  });
  const res = await api.get(`/api/clients/${clientId}/data-health/queue${qs}`, {
    headers: { 'x-client-id': clientId },
  });
  const d = (res.data || {}) as Record<string, unknown>;
  return {
    items: Array.isArray(d.items) ? (d.items as DataHealthItem[]) : [],
    total: typeof d.total === 'number' ? d.total : 0,
    page: typeof d.page === 'number' ? d.page : 1,
    pageSize: typeof d.pageSize === 'number' ? d.pageSize : 50,
    hasMore: !!d.hasMore,
    scenario: (d.scenario as RevisionScenario) || scenario,
  };
}

export async function reviveItem(
  clientId: string,
  idCatalogo: string,
  args: { scenario: RevisionScenario; comment?: string }
): Promise<{ success: boolean; feedback_id?: number }> {
  const res = await api.post(
    `/api/clients/${clientId}/data-health/items/${encodeURIComponent(idCatalogo)}/revive`,
    args,
    { headers: { 'x-client-id': clientId } }
  );
  return res.data || { success: false };
}

export async function confirmRejectItem(
  clientId: string,
  idCatalogo: string,
  args: { scenario: RevisionScenario; comment?: string }
): Promise<{ success: boolean; feedback_id?: number }> {
  const res = await api.post(
    `/api/clients/${clientId}/data-health/items/${encodeURIComponent(idCatalogo)}/confirm-reject`,
    args,
    { headers: { 'x-client-id': clientId } }
  );
  return res.data || { success: false };
}

export async function catalogManualItem(
  clientId: string,
  idCatalogo: string,
  args: {
    scenario: RevisionScenario;
    comment?: string;
    fields: Partial<{
      catalog_brand: string | null;
      catalog_category: string | null;
      subcategory: string | null;
      segmento: string | null;
      contenido: string | null;
      udm: string | null;
    }>;
  }
): Promise<{ success: boolean; feedback_id?: number; row?: Record<string, unknown> }> {
  const res = await api.post(
    `/api/clients/${clientId}/data-health/items/${encodeURIComponent(idCatalogo)}/catalog-manual`,
    args,
    { headers: { 'x-client-id': clientId } }
  );
  return res.data || { success: false };
}

export interface BulkRevisionResult {
  success: boolean;
  action?: 'revive' | 'confirm_reject';
  requested?: number;
  processed?: number;
  feedback_inserted?: number;
  confirm_required?: boolean;
  count?: number;
}

export async function bulkRevisionAction(
  clientId: string,
  args: {
    ids: string[];
    action: 'revive' | 'confirm_reject';
    scenario: RevisionScenario;
    comment?: string;
    confirmed?: boolean;
  }
): Promise<BulkRevisionResult> {
  const res = await api.post(
    `/api/clients/${clientId}/data-health/bulk-action`,
    args,
    { headers: { 'x-client-id': clientId } }
  );
  return res.data || { success: false };
}

export async function getRevisionHistory(
  clientId: string,
  idCatalogo: string,
  limit = 100
): Promise<{ events: RevisionHistoryEvent[]; count: number }> {
  const res = await api.get(
    `/api/clients/${clientId}/data-health/items/${encodeURIComponent(idCatalogo)}/history${buildQuery({ limit })}`,
    { headers: { 'x-client-id': clientId } }
  );
  const d = (res.data || {}) as Record<string, unknown>;
  const events = Array.isArray(d.events) ? (d.events as RevisionHistoryEvent[]) : [];
  return { events, count: events.length };
}

// ----------------------------------------------------------------------------
// Error helpers
// ----------------------------------------------------------------------------

/**
 * Detecta si un error de axios indica que el endpoint backend todavía no
 * está deployado. Útil para mostrar un fallback "Backend pendiente".
 */
export function isBackendUnavailable(err: unknown): boolean {
  const e = err as { response?: { status?: number }; code?: string };
  if (!e) return false;
  if (e.response?.status === 404) return true;
  if (e.response?.status === 501) return true;
  if (e.code === 'ECONNREFUSED' || e.code === 'ERR_NETWORK') return true;
  return false;
}

export function errorMessage(err: unknown): string {
  const e = err as { response?: { data?: { error?: string } }; message?: string };
  return e?.response?.data?.error || e?.message || 'Error desconocido';
}
